import React, { useState, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faEyeSlash, faEye, faSync, faPlus, faChevronDown, faChevronRight, faExclamationTriangle, faSearch } from '@fortawesome/free-solid-svg-icons';
import { Card, Form, Button, Alert, Table, Badge, Col, Row, Modal, InputGroup, ProgressBar, Pagination } from '@themesberg/react-bootstrap';
import api from '../../utils/api';
import { Link } from 'react-router-dom';
import { Routes } from "../../routes";

export default () => {
    const [reels, setReels] = useState([]);
    const [filteredReels, setFilteredReels] = useState([]);
    const [filterType, setFilterType] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [actionLoading, setActionLoading] = useState(null);
    const [fakeAccounts, setFakeAccounts] = useState([]);
    const [currentAuthorPage, setCurrentAuthorPage] = useState(1);
    const [authorSearchText, setAuthorSearchText] = useState("");
    const authorsPerPage = 12;

    // Kategorize görünüm state'leri
    const [expandedUsers, setExpandedUsers] = useState({}); // userId -> kaç tane gösteriliyor (0 = kapalı)
    const EXPAND_STEP = 15;

    // Toplu silme modal state'leri
    const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
    const [showUserDeleteModal, setShowUserDeleteModal] = useState(false);
    const [deleteTargetUser, setDeleteTargetUser] = useState(null); // {userId, userName, count}
    const [bulkDeleteProgress, setBulkDeleteProgress] = useState(null); // {current, total}

    // Load Reels
    useEffect(() => {
        const fetchReels = async () => {
            try {
                const res = await api.get('/content/admin-list?type=video');
                const reelsList = res.data?.contents || [];
                setReels(reelsList);
                setFilteredReels(reelsList);
            } catch (err) {
                console.error("Error loading reels:", err);
                setError("Reels yüklenirken hata oluştu: " + err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchReels();
    }, []);

    // Load Fake Accounts
    useEffect(() => {
        const fetchAccounts = async () => {
            try {
                const res = await api.get('/users/admin-list');
                const usersList = res.data?.users || res.data || [];
                const accounts = usersList
                    .filter(data => data.isTestUser === true || data.isFake === true)
                    .map(data => ({ id: data.id || data._id, name: data.name || data.firstName || 'Bilinmeyen' }));
                setFakeAccounts(accounts);
            } catch (err) {
                console.error("Error loading fake accounts:", err);
            }
        };
        fetchAccounts();
    }, []);

    // Filtering
    useEffect(() => {
        if (!filterType) {
            setFilteredReels(reels);
            return;
        }

        const now = new Date();
        let filtered = [...reels];

        if (filterType === "fakeAccounts") {
            const fakeAccountIds = new Set(fakeAccounts.map(acc => acc.id));
            filtered = filtered.filter(reel => fakeAccountIds.has(reel.userId));
        } else if (filterType === "expired") {
            filtered = filtered.filter(reel => reel.expiresAt && reel.expiresAt < now);
        } else if (filterType === "active") {
            filtered = filtered.filter(reel => !reel.expiresAt || reel.expiresAt >= now);
        }

        setFilteredReels(filtered);
    }, [filterType, reels, fakeAccounts]);

    // Filtre değiştiğinde sayfayı sıfırla
    useEffect(() => {
        setCurrentAuthorPage(1);
    }, [filterType, authorSearchText]);

    // Reels'leri kullanıcıya göre grupla
    const groupedReels = React.useMemo(() => {
        const groups = {};
        filteredReels.forEach(reel => {
            const key = reel.userId || 'unknown';
            if (!groups[key]) {
                groups[key] = {
                    userId: key,
                    userName: reel.userName || 'Bilinmeyen',
                    userProfileImageUrl: reel.userProfileImageUrl,
                    reels: []
                };
            }
            groups[key].reels.push(reel);
        });
        // En çok reels'i olan kullanıcıyı üste koy
        return Object.values(groups).sort((a, b) => b.reels.length - a.reels.length);
    }, [filteredReels]);

    // Arama filtresi uygulanmış yazarlar
    const searchedAuthors = React.useMemo(() => {
        if (!authorSearchText) return groupedReels;
        return groupedReels.filter(g => g.userName.toLowerCase().includes(authorSearchText.toLowerCase()));
    }, [groupedReels, authorSearchText]);

    // Sayfalanmış yazarlar
    const paginatedAuthors = React.useMemo(() => {
        const startIndex = (currentAuthorPage - 1) * authorsPerPage;
        return searchedAuthors.slice(startIndex, startIndex + authorsPerPage);
    }, [searchedAuthors, currentAuthorPage]);

    const totalAuthorPages = Math.ceil(searchedAuthors.length / authorsPerPage);

    const formatDate = (date) => {
        if (!date) return "-";
        return new Date(date).toLocaleString('tr-TR');
    };

    const isExpired = (reel) => {
        if (!reel.expiresAt) return false;
        return new Date(reel.expiresAt) < new Date();
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Bu Reels videosunu kalıcı olarak silmek istediğinizden emin misiniz?")) return;
        setActionLoading(id);
        try {
            await api.delete(`/content/admin-delete/${id}`);
            setReels(prev => prev.filter(r => r.id !== id));
            setFilteredReels(prev => prev.filter(r => r.id !== id));
            setSuccess("Reels başarıyla silindi.");
        } catch (err) {
            setError("Silme hatası: " + err.message);
        } finally {
            setActionLoading(null);
        }
    };

    const handleToggleVisibility = async (reel) => {
        const newVisibility = !(reel.isVisibleToOthers !== false);
        setActionLoading(reel.id);
        try {
            await api.post(`/content/admin-update/${reel.id}`, { isVisibleToOthers: newVisibility });
            const updateState = (prev) => prev.map(r => r.id === reel.id ? { ...r, isVisibleToOthers: newVisibility } : r);
            setReels(updateState);
            setFilteredReels(updateState);
            setSuccess(`Reels artık ${newVisibility ? 'herkese açık' : 'gizli'}.`);
        } catch (err) {
            setError("Güncelleme hatası: " + err.message);
        } finally {
            setActionLoading(null);
        }
    };

    const handleRepublish = async (reel) => {
        if (!window.confirm("Bu Reels videosunu güncel tarihle yeniden yayınlamak istiyor musunuz?")) return;
        setActionLoading(reel.id);
        try {
            const { id, ...reelData } = reel;
            const res = await api.post('/content/admin-create', {
                ...reelData,
                type: 'reel',
                isVisibleToOthers: true,
                expiresAt: null
            });
            const newReel = res.data?.content || res.data;
            if (newReel) {
                setReels(prev => [newReel, ...prev]);
                setFilteredReels(prev => [newReel, ...prev]);
            }
            setSuccess("Reels başarıyla yeniden yayınlandı.");
        } catch (err) {
            setError("Yeniden yayınlama hatası: " + err.message);
        } finally {
            setActionLoading(null);
        }
    };

    // Kullanıcının tüm reels'lerini sil
    const handleDeleteUserReels = async () => {
        if (!deleteTargetUser) return;
        const { userId, count } = deleteTargetUser;

        setBulkDeleteProgress({ current: 0, total: count });
        setShowUserDeleteModal(false);

        try {
            const userReels = filteredReels.filter(r => r.userId === userId);
            let deleted = 0;
            for (const reel of userReels) {
                await api.delete(`/content/admin-delete/${reel.id}`);
                deleted++;
                setBulkDeleteProgress({ current: deleted, total: count });
            }
            setReels(prev => prev.filter(r => r.userId !== userId));
            setFilteredReels(prev => prev.filter(r => r.userId !== userId));
            setSuccess(`${deleted} reels başarıyla silindi.`);
        } catch (err) {
            setError("Toplu silme hatası: " + err.message);
        } finally {
            setBulkDeleteProgress(null);
            setDeleteTargetUser(null);
        }
    };

    // TÜM reels'leri sil
    const handleBulkDeleteAll = async () => {
        setShowBulkDeleteModal(false);
        const total = filteredReels.length;
        setBulkDeleteProgress({ current: 0, total });

        try {
            let deleted = 0;
            for (const reel of filteredReels) {
                await api.delete(`/content/admin-delete/${reel.id}`);
                deleted++;
                setBulkDeleteProgress({ current: deleted, total });
            }
            const deletedIds = new Set(filteredReels.map(r => r.id));
            setReels(prev => prev.filter(r => !deletedIds.has(r.id)));
            setFilteredReels([]);
            setSuccess(`${deleted} reels tamamen silindi.`);
        } catch (err) {
            setError("Toplu silme hatası: " + err.message);
        } finally {
            setBulkDeleteProgress(null);
        }
    };

    // Expand/collapse toggle
    const toggleExpand = (userId) => {
        setExpandedUsers(prev => {
            const current = prev[userId] || 0;
            if (current === 0) {
                // İlk kez açılıyor → EXPAND_STEP kadar göster
                return { ...prev, [userId]: EXPAND_STEP };
            } else {
                // Kapatmak için 0 yap
                return { ...prev, [userId]: 0 };
            }
        });
    };

    const showMore = (userId, totalCount) => {
        setExpandedUsers(prev => {
            const current = prev[userId] || 0;
            return { ...prev, [userId]: Math.min(current + EXPAND_STEP, totalCount) };
        });
    };

    return (
        <>
            <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
                <div className="d-block mb-4 mb-md-0">
                    <h4>Reels Videoları</h4>
                    <p className="mb-0 text-muted">
                        Uygulamadaki Reels videolarını yönetin.
                        {!loading && <span className="ms-2"><strong>{filteredReels.length}</strong> video, <strong>{groupedReels.length}</strong> kullanıcı</span>}
                    </p>
                </div>
                <div className="btn-toolbar mb-2 mb-md-0 gap-2">
                    {filteredReels.length > 0 && (
                        <Button
                            variant="danger"
                            size="sm"
                            onClick={() => setShowBulkDeleteModal(true)}
                            disabled={bulkDeleteProgress !== null}
                        >
                            <FontAwesomeIcon icon={faTrash} className="me-2" />
                            Tümünü Sil ({filteredReels.length})
                        </Button>
                    )}
                    <Button as={Link} to={Routes.ReelsAdd.path} variant="primary" size="sm">
                        <FontAwesomeIcon icon={faPlus} className="me-2" /> Yeni Reels
                    </Button>
                </div>
            </div>

            {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}
            {success && <Alert variant="success" onClose={() => setSuccess(false)} dismissible>{success}</Alert>}

            {/* Toplu silme progress */}
            {bulkDeleteProgress && (
                <Alert variant="warning" className="mb-3">
                    <div className="d-flex align-items-center mb-2">
                        <FontAwesomeIcon icon={faTrash} className="me-2" spin />
                        Siliniyor: {bulkDeleteProgress.current} / {bulkDeleteProgress.total}
                    </div>
                    <ProgressBar
                        now={(bulkDeleteProgress.current / bulkDeleteProgress.total) * 100}
                        label={`${Math.round((bulkDeleteProgress.current / bulkDeleteProgress.total) * 100)}%`}
                        variant="danger"
                    />
                </Alert>
            )}

            <Card border="light" className="shadow-sm">
                <Card.Body className="pt-0">
                    <div className="table-settings mb-4">
                        <Row className="align-items-center justify-content-between">
                            <Col xs={8} md={4} className="mt-3">
                                <Form.Select value={filterType || ""} onChange={(e) => setFilterType(e.target.value || null)}>
                                    <option value="">Filtrele: Tümü</option>
                                    <option value="fakeAccounts">Sadece Fake Hesaplar</option>
                                    <option value="active">Aktif Olanlar</option>
                                    <option value="expired">Süresi Dolanlar</option>
                                </Form.Select>
                            </Col>
                            <Col xs={12} md={4} className="mt-3">
                                <InputGroup>
                                    <InputGroup.Text>
                                        <FontAwesomeIcon icon={faSearch} />
                                    </InputGroup.Text>
                                    <Form.Control
                                        type="text"
                                        placeholder="İsim ile veya kullanıcı adı ile ara..."
                                        value={authorSearchText}
                                        onChange={(e) => setAuthorSearchText(e.target.value)}
                                    />
                                </InputGroup>
                            </Col>
                        </Row>
                    </div>

                    {loading ? (
                        <div className="text-center py-5">Yükleniyor...</div>
                    ) : paginatedAuthors.length === 0 ? (
                        <div className="text-center py-5 text-muted">Henüz reels yok.</div>
                    ) : (
                        <>
                            {paginatedAuthors.map((group) => {
                                const visibleCount = expandedUsers[group.userId] || 0;
                                const isOpen = visibleCount > 0;
                                const visibleReels = group.reels.slice(0, visibleCount);
                                const remaining = group.reels.length - visibleCount;

                                return (
                                    <div key={group.userId} className="mb-2">
                                        {/* Kullanıcı Başlık Satırı */}
                                        <div
                                            className="d-flex align-items-center justify-content-between p-3"
                                            style={{
                                                background: isOpen ? '#e8edf5' : '#f8f9fa',
                                                borderRadius: isOpen ? '8px 8px 0 0' : '8px',
                                                cursor: 'pointer',
                                                borderLeft: '4px solid',
                                                borderLeftColor: isOpen ? '#4e73df' : '#adb5bd',
                                                transition: 'all 0.2s'
                                            }}
                                            onClick={() => toggleExpand(group.userId)}
                                        >
                                            <div className="d-flex align-items-center">
                                                <FontAwesomeIcon
                                                    icon={isOpen ? faChevronDown : faChevronRight}
                                                    className="me-3 text-muted"
                                                    style={{ width: '12px' }}
                                                />
                                                <img
                                                    src={group.userProfileImageUrl || "/avatar.png"}
                                                    className="avatar rounded-circle me-3"
                                                    style={{ width: 40, height: 40, objectFit: 'cover' }}
                                                    alt=""
                                                />
                                                <div>
                                                    <span className="fw-bold text-dark">{group.userName}</span>
                                                    <Badge bg="secondary" className="ms-2">{group.reels.length} reels</Badge>
                                                </div>
                                            </div>
                                            <div className="d-flex align-items-center gap-2" onClick={e => e.stopPropagation()}>
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    onClick={() => {
                                                        setDeleteTargetUser({
                                                            userId: group.userId,
                                                            userName: group.userName,
                                                            count: group.reels.length
                                                        });
                                                        setShowUserDeleteModal(true);
                                                    }}
                                                    disabled={bulkDeleteProgress !== null}
                                                    title={`${group.userName} - Tüm reels'leri sil`}
                                                >
                                                    <FontAwesomeIcon icon={faTrash} className="me-1" />
                                                    Hepsini Sil
                                                </Button>
                                            </div>
                                        </div>

                                        {/* Reels Tablosu (Açılırsa) */}
                                        {isOpen && (
                                            <div style={{
                                                background: '#ffffff',
                                                borderRadius: '0 0 8px 8px',
                                                border: '1px solid #dee2e6',
                                                borderTop: 'none'
                                            }}>
                                                <Table hover className="user-table align-items-center mb-0" size="sm">
                                                    <thead>
                                                        <tr>
                                                            <th className="border-bottom" style={{ width: '80px', color: '#212529' }}>Video</th>
                                                            <th className="border-bottom" style={{ color: '#212529' }}>Açıklama</th>
                                                            <th className="border-bottom" style={{ color: '#212529' }}>Tarih</th>
                                                            <th className="border-bottom" style={{ color: '#212529' }}>Bitiş</th>
                                                            <th className="border-bottom" style={{ color: '#212529' }}>Beğeni</th>
                                                            <th className="border-bottom" style={{ color: '#212529' }}>Durum</th>
                                                            <th className="border-bottom" style={{ width: '140px', color: '#212529' }}>İşlem</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {visibleReels.map(reel => (
                                                            <tr key={reel.id}>
                                                                <td>
                                                                    {reel.thumbnailUrl ? (
                                                                        <img src={reel.thumbnailUrl} alt="Thumbnail" style={{ width: 50, height: 80, objectFit: 'cover', borderRadius: 6 }} />
                                                                    ) : (
                                                                        <video src={reel.videoUrl} style={{ width: 50, height: 80, objectFit: 'cover', borderRadius: 6 }} muted />
                                                                    )}
                                                                </td>
                                                                <td style={{ color: '#e0e6ed' }}><div style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{reel.description || '-'}</div></td>
                                                                <td style={{ color: '#ccd6e0' }}><small>{formatDate(reel.createdAt)}</small></td>
                                                                <td style={{ color: '#ccd6e0' }}><small>{reel.expiresAt ? formatDate(reel.expiresAt) : "Süresiz"}</small></td>
                                                                <td style={{ color: '#e8d060' }}>❤️ {reel.likeCount || 0}</td>
                                                                <td>
                                                                    {isExpired(reel) ? (
                                                                        <Badge bg="danger">Doldu</Badge>
                                                                    ) : (reel.isVisibleToOthers === false ? (
                                                                        <Badge bg="warning">Gizli</Badge>
                                                                    ) : (
                                                                        <Badge bg="success">Yayında</Badge>
                                                                    ))}
                                                                </td>
                                                                <td>
                                                                    <div className="btn-group">
                                                                        <Button variant="outline-primary" size="sm" onClick={() => handleToggleVisibility(reel)} disabled={actionLoading === reel.id} title={reel.isVisibleToOthers === false ? "Yayınla" : "Gizle"}>
                                                                            <FontAwesomeIcon icon={reel.isVisibleToOthers === false ? faEye : faEyeSlash} />
                                                                        </Button>
                                                                        <Button variant="outline-info" size="sm" onClick={() => handleRepublish(reel)} disabled={actionLoading === reel.id} title="Yeniden Paylaş">
                                                                            <FontAwesomeIcon icon={faSync} />
                                                                        </Button>
                                                                        <Button variant="outline-danger" size="sm" onClick={() => handleDelete(reel.id)} disabled={actionLoading === reel.id} title="Sil">
                                                                            <FontAwesomeIcon icon={faTrash} />
                                                                        </Button>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>

                                                {/* +15 Daha Göster butonu */}
                                                {remaining > 0 && (
                                                    <div className="text-center py-3">
                                                        <Button
                                                            variant="outline-primary"
                                                            size="sm"
                                                            onClick={() => showMore(group.userId, group.reels.length)}
                                                        >
                                                            <FontAwesomeIcon icon={faChevronDown} className="me-2" />
                                                            +{Math.min(remaining, EXPAND_STEP)} daha göster
                                                            <span className="text-muted ms-2">({remaining} kalan)</span>
                                                        </Button>
                                                    </div>
                                                )}
                                            </div>
                                        )
                                        }
                                    </div>
                                );
                            })}

                            {/* Pagination UI */}
                            {totalAuthorPages > 1 && (
                                <div className="d-flex justify-content-between align-items-center p-3 mt-3 bg-light rounded shadow-sm">
                                    <small className="text-muted">Toplam {searchedAuthors.length} yazar üzerinden {currentAuthorPage}. sayfa gösteriliyor</small>
                                    <Pagination size="sm" className="mb-0">
                                        <Pagination.Prev
                                            onClick={() => setCurrentAuthorPage(p => Math.max(1, p - 1))}
                                            disabled={currentAuthorPage === 1}
                                        >
                                            Geri
                                        </Pagination.Prev>
                                        {Array.from({ length: Math.min(5, totalAuthorPages) }, (_, i) => {
                                            let pageNum;
                                            if (totalAuthorPages <= 5) pageNum = i + 1;
                                            else if (currentAuthorPage <= 3) pageNum = i + 1;
                                            else if (currentAuthorPage >= totalAuthorPages - 2) pageNum = totalAuthorPages - 4 + i;
                                            else pageNum = currentAuthorPage - 2 + i;

                                            return (
                                                <Pagination.Item
                                                    key={pageNum}
                                                    active={currentAuthorPage === pageNum}
                                                    onClick={() => setCurrentAuthorPage(pageNum)}
                                                >
                                                    {pageNum}
                                                </Pagination.Item>
                                            );
                                        })}
                                        <Pagination.Next
                                            onClick={() => setCurrentAuthorPage(p => Math.min(totalAuthorPages, p + 1))}
                                            disabled={currentAuthorPage === totalAuthorPages}
                                        >
                                            İleri
                                        </Pagination.Next>
                                    </Pagination>
                                </div>
                            )}
                        </>
                    )}
                </Card.Body>
            </Card >

            {/* Tümünü Sil Modal */}
            < Modal show={showBulkDeleteModal} onHide={() => setShowBulkDeleteModal(false)
            }>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FontAwesomeIcon icon={faExclamationTriangle} className="text-danger me-2" />
                        Tüm Reels'leri Sil
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="mb-0">
                        <strong>{filteredReels.length}</strong> adet Reels videosunu kalıcı olarak silmek istediğinizden emin misiniz?
                        {filterType && <span className="text-warning d-block mt-2">Not: Sadece filtrelenmiş ({filterType}) Reels'ler silinecek.</span>}
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowBulkDeleteModal(false)}>İptal</Button>
                    <Button variant="danger" onClick={handleBulkDeleteAll}>
                        Evet, {filteredReels.length} Reels'i Sil
                    </Button>
                </Modal.Footer>
            </Modal >

            {/* Kullanıcının Tüm Reels'lerini Sil Modal */}
            < Modal show={showUserDeleteModal} onHide={() => setShowUserDeleteModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FontAwesomeIcon icon={faExclamationTriangle} className="text-danger me-2" />
                        Kullanıcı Reels'lerini Sil
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>
                        <strong>{deleteTargetUser?.userName}</strong> kullanıcısının <strong>{deleteTargetUser?.count}</strong> adet Reels videosunu kalıcı olarak silmek istediğinizden emin misiniz?
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => { setShowUserDeleteModal(false); setDeleteTargetUser(null); }}>İptal</Button>
                    <Button variant="danger" onClick={handleDeleteUserReels}>
                        Evet, {deleteTargetUser?.count} Reels'i Sil
                    </Button>
                </Modal.Footer>
            </Modal >
        </>
    );
};
